/**
 * UpgradePrompt
 * Shown when a plan limit (e.g. the free tier's 300-feed cap) is hit.
 *
 * Every tier this deployment can charge for is offered, in catalogue order, with
 * the first one highlighted as the recommended step. The catalogue is what
 * decides that order (`GET /billing` sorts by `sort_order`), so the smallest paid
 * step comes first without this component naming a plan: a hardcoded id is how
 * the old "Startup" one went on being offered after the catalogue was split.
 *
 * Starter is recommended rather than assumed: a customer who has just outgrown
 * 300 calendars is usually best served by the next step up, but the tiers above
 * it are still buyable, because a project that has outgrown the free tier by a
 * lot must not have to buy the smallest plan first and then upgrade again.
 *
 * Prices come from the plan objects, i.e. from the amounts the server read at the
 * payment provider. This prompt used to describe the tariff in hardcoded copy
 * ("Up to 3,000 feeds · priced per 1,000 polls"), which kept advertising a price
 * the catalogue no longer charged — stale words in the one place a customer
 * decides to pay. A tier with no price shows no price line rather than an
 * invented amount.
 *
 * Props:
 *   usedFeeds, totalFeeds
 *   tiers: buyable plan options from GET /billing, in catalogue order
 *   onUpgrade(planId) — opens the period screen, it does not charge
 *   onContactSales()
 *   onRemoveFeed()
 */
export default function UpgradePrompt({
  usedFeeds = 300,
  totalFeeds = 300,
  tiers = [],
  onUpgrade,
  onContactSales,
  onRemoveFeed,
}) {
  // Enterprise is sold above whatever the largest self-serve tier allows, so the
  // number is read from the tiers rather than frozen into this sentence.
  const topLimit = tiers.reduce(
    (max, tier) => (tier.feedLimit != null && tier.feedLimit > max ? tier.feedLimit : max),
    0,
  );

  return (
    <div style={{ maxWidth: 420, margin: "0 auto", background: "var(--surface-1)", borderRadius: "var(--radius-lg)", padding: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ color: "var(--text-warning)" }}>⚠</span>
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: 0 }}>You're at your free plan limit</p>
      </div>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 14px" }}>
        {usedFeeds} / {totalFeeds} feeds subscribed. New feeds won't be polled until you upgrade or remove one.
      </p>

      <div style={{ height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden", marginBottom: 4 }}>
        <div style={{ height: "100%", width: `${Math.min(100, (usedFeeds / totalFeeds) * 100)}%`, background: "var(--fill-warning)" }} />
      </div>
      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "0 0 16px" }}>
        {usedFeeds} of {totalFeeds} feeds · resets on your billing date, not by upgrading
      </p>

      {tiers.length === 0 ? (
        // Nothing is for sale here — a deployment without a price, or one whose
        // price lookup failed. Saying so beats an empty card, and beats inventing
        // a plan the server cannot charge for.
        <p
          data-testid="upgrade-no-tiers"
          style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 10px" }}
        >
          No paid plan can be bought on this server yet.
        </p>
      ) : null}

      {tiers.map((tier, index) => (
        <TierCard
          key={tier.id}
          tier={tier}
          recommended={index === 0}
          onUpgrade={onUpgrade}
        />
      ))}

      <div className="ical-row">
        <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>Enterprise</p>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 10px" }}>
          {topLimit > 0 ? `Above ${topLimit.toLocaleString()} feeds` : "Unlimited feeds"} · volume pricing
        </p>
        <button className="ical-btn" style={{ width: "100%" }} onClick={onContactSales}>Talk to us</button>
      </div>

      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textAlign: "center", margin: "14px 0 0" }}>
        Or{" "}
        <button type="button" onClick={onRemoveFeed} style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}>
          remove an existing feed
        </button>{" "}
        to free up space.
      </p>
    </div>
  );
}

/** TierCard is one buyable tier: what it allows, what it costs, and the way in. */
function TierCard({ tier, recommended, onUpgrade }) {
  const limit = tier.feedLimit == null ? "Unlimited feeds" : `Up to ${tier.feedLimit.toLocaleString()} feeds`;
  const offer = describeOffer(tier);

  return (
    <div
      style={
        recommended
          ? { border: "2px solid var(--border-accent)", borderRadius: "var(--radius)", padding: 14, marginBottom: 10 }
          : { border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 14, marginBottom: 10 }
      }
    >
      {recommended ? (
        <span className="ical-badge" style={{ background: "var(--bg-accent)", color: "var(--text-accent)" }}>
          Most popular
        </span>
      ) : null}
      <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: recommended ? "10px 0 2px" : "0 0 2px" }}>
        {tier.name}
      </p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 10px" }}>{limit}</p>
      {offer ? (
        <p
          data-testid={`upgrade-offer-${tier.id}`}
          style={{ fontWeight: 500, fontSize: "var(--text-base)", margin: "0 0 4px" }}
        >
          {offer}
        </p>
      ) : null}
      {tier.rateLabel ? (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "0 0 10px" }}>
          {tier.rateLabel}
        </p>
      ) : null}
      <button
        className={recommended ? "ical-btn ical-btn-primary" : "ical-btn"}
        style={{ width: "100%" }}
        onClick={() => onUpgrade?.(tier.id)}
      >
        Upgrade to {tier.name}
      </button>
    </div>
  );
}

/**
 * describeOffer states what a tier costs, from the amounts the server read at
 * the payment provider: "€39/month or €399/year — save 15%".
 *
 * It returns null when the deployment has no monthly price for the tier, which
 * is the caller's cue to show no price at all. A plan sold only monthly gets no
 * yearly half-sentence, and a yearly price that is not cheaper than twelve
 * months carries no "save" claim.
 */
export function describeOffer(plan) {
  const monthly = plan?.prices?.monthly;
  const yearly = plan?.prices?.yearly;
  if (!monthly?.available) return null;
  if (!yearly?.available) return `${monthly.amountLabel}/month`;
  const saving = yearly.discountPercent > 0 ? ` — save ${yearly.discountPercent}%` : "";
  return `${monthly.amountLabel}/month or ${yearly.amountLabel}/year${saving}`;
}
